/**
 * @file record-operation.tsx
 * 记录操作组件，用于处理数据记录的相关操作，支持确认弹窗和触发操作
 * @module @yimoka/antd
 * @author Yimoka Team
 * @since 1.0.0
 *
 * @example
 * ```tsx
 * // 基础用法
 * <RecordOperation
 *   operation="delete"
 *   store={store}
 *   trigger={{ text: '删除' }}
 *   popconfirm={{ title: '确认删除该记录？' }}
 * />
 *
 * // 带默认值的操作
 * <RecordOperation
 *   operation="update"
 *   store={store}
 *   defaultValues={{ status: 'active' }}
 *   trigger={{ text: '更新状态' }}
 * />
 * ```
 */

import { observer, useExpressionScope } from '@formily/react';
import { IEntityOpProps, Trigger, TriggerProps, useAPIExecutor, useNotifier, useStore } from '@yimoka/react';
import { IAnyObject, isSuccess } from '@yimoka/shared';
import { BaseStore, getEntityStore, IAPIKey, IStore, refreshParentStore } from '@yimoka/store';
import { Button, Popconfirm, PopconfirmProps } from 'antd';
import { pick } from 'lodash-es';
import React, { useMemo, useRef, useState } from 'react';

import { useLocaleComponent } from '../hooks/use-locale';

/** 默认的ID字段名 */
const DEFAULT_ID_KEY = 'id';

/**
 * RecordOperation 组件
 * 用于处理记录相关的操作，支持确认弹窗和触发操作
 * @component
 * @param {RecordOperationProps} props - 组件属性
 * @returns {JSX.Element} 返回操作按钮组件
 *
 * @example
 * ```tsx
 * // 带自定义配置的操作
 * <RecordOperation
 *   operation="custom"
 *   store={store}
 *   config={{ idKey: 'customId' }}
 *   trigger={{ text: '自定义操作' }}
 * />
 * ```
 */
export const RecordOperation = observer((props: RecordOperationProps) => {
  const { parentStore, store, popconfirm, trigger, operation, config, defaultValues = {}, record, isRefresh = true } = props;
  const locale = useLocaleComponent('Common');
  const { $config, $record, $index } = useExpressionScope() ?? {};
  const pStore = useStore(parentStore);
  const [loading, setLoading] = useState(false);
  const runningRef = useRef(false);
  const apiExecutor = useAPIExecutor();
  const notifier = useNotifier();
  const scope = useExpressionScope();

  /**
   * 执行操作的核心函数
   * 初始化store并执行操作，支持操作成功后刷新父级数据
   * @async
   * @returns {Promise<void>}
   */
  const run = async () => {
    // loading state 不能阻止同一帧内的连续事件，必须用同步 ref 锁定一次操作。
    if (runningRef.current) return;
    runningRef.current = true;
    setLoading(true);
    try {
      // 初始化与参数装配也可能因坏 Schema 抛错，必须置于 finally 保护内释放同步锁。
      const runStoreOrConfig = getEntityStore(store, operation, config ?? $config, true);
      const runStore = runStoreOrConfig instanceof BaseStore ? runStoreOrConfig : new BaseStore(runStoreOrConfig);
      !runStore.apiExecutor && (runStore.apiExecutor = apiExecutor);
      !runStore.notifier && (runStore.notifier = notifier);
      // 合并默认值
      runStore.setDefaultValues(defaultValues);
      const idKey = (config ?? $config)?.idKey ?? DEFAULT_ID_KEY;
      // 设置操作值
      const newValues = pick({ $index, ...$record, ...record }, idKey, Object.keys(runStore.defaultValues));
      runStore.setValues(newValues);
      const res = await runStore.fetch();
      (isSuccess(res) && isRefresh) && await refreshParentStore(pStore, scope);
    } catch (error) {
      // Schema 初始化和自定义 Store 仍可能抛异常，统一通知并吞掉事件回调中的未处理 Promise。
      notifier?.('error', error instanceof Error ? error.message : String(error));
    } finally {
      runningRef.current = false;
      setLoading(false);
    }
  };

  /**
   * 优化 popconfirm 配置
   * 处理 popconfirm 属性，支持布尔值和对象两种配置方式
   * @returns {PopconfirmProps | null} 处理后的 popconfirm 配置
   */
  const popconfirmProps = useMemo(() => {
    if (!popconfirm) return null;
    return popconfirm === true ? { title: locale.confirm } : popconfirm;
  }, [popconfirm, locale]);

  if (popconfirmProps) {
    return (
      <Popconfirm
        {...popconfirmProps}
        okButtonProps={{ ...popconfirmProps.okButtonProps, loading: loading || popconfirmProps.okButtonProps?.loading }}
        onConfirm={(e) => {
          popconfirmProps.onConfirm?.(e);
          void run();
        }}>
        {/* fix Warning: findDOMNode is deprecated and will be removed in the next major release. Instead, */}
        <>
          <Trigger component={Button} {...trigger} loading={loading || trigger?.loading} />
        </>
      </Popconfirm>
    );
  }

  return (
    <Trigger
      component={Button}
      {...trigger}
      loading={loading || trigger?.loading}
      onTrig={(...args) => {
        void run();
        trigger?.onTrig?.(...args);
      }}
    />
  );
});

/**
 * RecordOperation 组件属性接口
 * @interface RecordOperationProps
 * @extends {Pick<Partial<IEntityOpProps>, 'store' | 'operation' | 'config' | 'operation'>}
 */
export interface RecordOperationProps extends Pick<Partial<IEntityOpProps>, 'store' | 'operation' | 'config' | 'operation'> {
  /** 操作类型，必填 */
  operation: IAPIKey
  /** 父级 store，用于刷新数据，可选 */
  parentStore?: IStore;
  /** 是否在操作成功后刷新父级数据，可选 */
  isRefresh?: boolean;
  /** 默认值，可选 */
  defaultValues?: IAnyObject;
  /** 记录数据，可选 */
  record?: IAnyObject;
  /** 确认弹窗配置，可选 */
  popconfirm?: PopconfirmProps | boolean;
  /** 触发按钮配置，可选 */
  trigger?: TriggerProps;
}
